import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { CLIENT_PDF_SECTIONS, type ClientPdfSection } from "./clientPdfSections";

/**
 * O painel de montagem do PDF do cliente (#1772), à esquerda da prévia, como no
 * HTML do modelo da EF: as seções marcadas por padrão, a capa obrigatória e o que
 * não sai no PDF.
 */
export function ClientPdfPanel({
  selected,
  onToggle,
  pages,
  downloadHref,
  backHref,
}: {
  selected: ReadonlySet<ClientPdfSection>;
  onToggle: (section: ClientPdfSection, checked: boolean) => void;
  /** Quantas páginas a prévia tem; `null` enquanto ela não está pronta. */
  pages: number | null;
  /** O endereço do "Gerar PDF"; `null` quando não há seção marcada. */
  downloadHref: string | null;
  /** O Diagnóstico do projeto, para o "Voltar". */
  backHref: string;
}) {
  const { t } = useTranslation();

  return (
    <aside
      data-testid="client-pdf-panel"
      className="w-[280px] shrink-0 space-y-4 overflow-y-auto border-r bg-card p-4"
    >
      {/* O mesmo "Voltar ao diagnóstico" das telas de detalhe (`DiagnosticDetailLayout`). */}
      <Link
        to={backHref}
        className="inline-flex items-center gap-2 rounded-md border border-[#d4dae3] px-3.5 py-2 text-[13px] font-semibold text-[#596b85] transition-colors hover:border-[#08254c] hover:text-[#08254c]"
      >
        <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
        {t("Back to the diagnostic")}
      </Link>

      <div>
        <p className="font-mono text-[10.5px] font-medium uppercase tracking-[0.12em] text-[#5a6b84]">
          {t("Build PDF for the client")}
        </p>
        <h1 className="text-[15px] font-semibold text-[#08254c]">{t("Sections")}</h1>
      </div>

      {/* Os tamanhos são os do HTML do modelo: 13 px nas seções, 12,5 px no aviso. */}
      <ul data-testid="client-pdf-sections" className="space-y-2.5">
        <li className="flex items-center gap-2">
          <Checkbox id="client-pdf-cover" checked disabled />
          <Label htmlFor="client-pdf-cover" className="text-[13px] font-normal text-[#08254c]">
            {t("Cover")}
          </Label>
        </li>
        {CLIENT_PDF_SECTIONS.map((section) => {
          const id = `client-pdf-${section.key}`;
          return (
            <li key={section.key} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={selected.has(section.key)}
                onCheckedChange={(checked) => onToggle(section.key, checked === true)}
              />
              <Label htmlFor={id} className="text-[13px] font-normal text-[#08254c]">
                {t(section.label)}
              </Label>
            </li>
          );
        })}
      </ul>

      <p
        data-testid="client-pdf-excluded"
        className="rounded-md bg-[#e6ecf4] px-3 py-[9px] text-[12.5px] text-[#08254c]"
      >
        <strong>{t("Not in the PDF:")}</strong>{" "}
        {t(
          "prices, hours, margins, justifications and conversion strategy, technical description of the code, the chart of accounts account by account.",
        )}
      </p>

      {pages != null && (
        <p className="text-xs text-[#5a6b84]">
          {t("{{count}} page(s) · A4 portrait", { count: pages })}
        </p>
      )}

      {/*
        Link direto, como o antigo "Exportar PDF": o navegador salva o arquivo pelo
        `Content-Disposition` da rota, e o login viaja sozinho. Sem seção marcada,
        o botão fica desabilitado: sem o parâmetro, a rota geraria o PDF completo.
      */}
      {downloadHref ? (
        <a href={downloadHref} className={GENERATE_CLASS}>
          {t("Generate PDF")}
        </a>
      ) : (
        <button type="button" disabled className={`${GENERATE_CLASS} cursor-not-allowed opacity-50`}>
          {t("Generate PDF")}
        </button>
      )}
    </aside>
  );
}

/** O botão amarelo do modelo, na largura do painel. */
const GENERATE_CLASS =
  "block w-full rounded-md bg-[#ffc83d] px-3 py-2 text-left text-sm font-semibold text-[#08254c] hover:bg-[#ffb300]";
